import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { DashboardPage } from '@/components/app/dashboard-page';
import {
  type MessagingPlatform,
  useConversationTabCounts,
} from '@/features/conversations/api';
import { ConversationDetail } from '@/features/conversations/components/conversation-detail';
import { ConversationEmpty } from '@/features/conversations/components/conversation-empty';
import {
  CONVERSATION_TAB_VALUES,
  type ConversationTabValue,
  ConversationTabs,
  DEFAULT_CONVERSATION_TAB,
} from '@/features/conversations/components/conversation-tabs';
import { ConversationsPanel } from '@/features/conversations/components/conversations-panel';
import { useMobileDashboardHeaderContent } from '@/features/mobile-dashboard-header';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSession } from '@/lib/session';
import { useBranchRoutes } from '@/lib/use-routes';
import { cn } from '@/lib/utils';
import { useCallback, useMemo, useState } from 'react';

const searchSchema = z.object({
  id: z.string().optional(),
  // The open view, in the URL so it is linkable and survives opening a
  // thread and coming back (on a phone the list unmounts while a thread is
  // open). Omitted opens the default view, From Ads.
  tab: z.enum(CONVERSATION_TAB_VALUES).optional().catch(undefined),
});

export const Route = createFileRoute(
  '/_authed/dashboard/l/$locationId/clients/inbox'
)({
  validateSearch: searchSchema.parse,
  component: ConversationsPage,
});

/**
 * The back control for an open conversation on a phone — a drill-down, so it
 * gets one. Mounted ONLY while a conversation is open: on the list the shared
 * page shell owns the header (title beside the branch picker), and a route that
 * published its own content there would replace it.
 */
function MobileConversationHeader({ onBack }: { onBack: () => void }) {
  const content = useMemo(() => ({ showBack: true, onBack }), [onBack]);
  useMobileDashboardHeaderContent(content);
  return null;
}

function ConversationsPage() {
  const { id: selectedId, tab: tabParam } = Route.useSearch();
  const navigate = useNavigate();
  const routes = useBranchRoutes();
  const { data: session } = useSession();
  const isMobile = useIsMobile();
  const tab = tabParam ?? DEFAULT_CONVERSATION_TAB;
  const setTab = useCallback(
    (next: ConversationTabValue) =>
      navigate({
        to: routes.clientsInbox,
        search: { id: selectedId, tab: next },
        replace: true,
      }),
    [navigate, routes, selectedId]
  );
  const [platform, setPlatform] = useState('all');
  // Same query key the panel reads, so this is one request, not two.
  const { counts: tabCounts } = useConversationTabCounts(
    platform === 'all' ? undefined : (platform as MessagingPlatform)
  );
  // Two panes side by side need the room. Below this (tablet widths) the list
  // and the chat take turns, like the phone does.
  const isSplit = useMediaQuery('(min-width: 1024px)');

  const clearSelection = useCallback(
    () => navigate({ to: routes.clientsInbox, search: { tab: tabParam } }),
    [navigate, routes, tabParam]
  );

  if (!isMobile) {
    return (
      <>
        <title>Inbox | Borrah</title>
        {/* The shared page shell, one step wider: two panes need the room. */}
        <DashboardPage title="Inbox" width="wide" fillHeight>
          {/*
            The views run across the top of the whole inbox, above both panes;
            search and the filter button head the conversation list itself.
          */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card">
            <ConversationTabs
              variant="bar"
              className="shrink-0 rounded-none border-0 border-b shadow-none"
              value={tab}
              onChange={setTab}
              counts={tabCounts}
            />
            <div className="flex min-h-0 flex-1">
              {isSplit || !selectedId ? (
                <div
                  className={cn(
                    'flex flex-col overflow-hidden border-r bg-muted/15',
                    isSplit
                      ? 'w-80 min-w-0 shrink-0 xl:w-88 2xl:w-96'
                      : 'min-w-0 flex-1'
                  )}
                >
                  <ConversationsPanel
                    variant="card"
                    platform={platform}
                    onPlatformChange={setPlatform}
                    tab={tab}
                    onTabChange={setTab}
                  />
                </div>
              ) : null}
              {isSplit || selectedId ? (
                <div className="flex min-w-0 flex-1 basis-0 flex-col overflow-hidden">
                  {selectedId ? (
                    <ConversationDetail
                      conversationId={selectedId}
                      currentUserId={session?.user?.id}
                      onDeleted={clearSelection}
                      onBack={isSplit ? undefined : clearSelection}
                    />
                  ) : (
                    <ConversationEmpty />
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </DashboardPage>
      </>
    );
  }

  return (
    <>
      <title>Inbox | Borrah</title>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {isMobile ? (
          selectedId ? (
            <>
              <MobileConversationHeader onBack={clearSelection} />
              <ConversationDetail
                conversationId={selectedId}
                currentUserId={session?.user?.id}
                onBack={clearSelection}
                showInlineBackButton={false}
                onDeleted={clearSelection}
                variant="mobile"
              />
            </>
          ) : (
            <ConversationsPanel
              variant="mobile"
              tab={tab}
              onTabChange={setTab}
            />
          )
        ) : selectedId ? (
          <ConversationDetail
            conversationId={selectedId}
            currentUserId={session?.user?.id}
            onDeleted={clearSelection}
          />
        ) : (
          <ConversationEmpty />
        )}
      </div>
    </>
  );
}
